array_utils = App.load 'core::utils/array'

class exports.RatingsValidator

    constructor: (@products, @all_columns, @column_groups) ->
        ###
        All the rules are defined here for now

        Validation rules are defined in the following format:

        - msg: The validation message that will be displayed if the cell
            contents are invalid. Any variables prefixed with a colon will be
            replaced with the appropriate value (e.g. ":column" will be
            replaced with the column's display_name, ":value" will be replaced
            with the cell contents, etc). Custom replacements can be defined on
            the result of isValid (defined below). Replacement values can be
            strings, numbers, or functions (the return value will be used).

        input_types (optional): An array of column input_types that this rule
            can be applied to. If defined, an error will be thrown if the rule
            is applied to any other input_type.

        isValid: A function that be called to determine if the cell's value is
            valid. The parameters passed to this function include the column
            definition, the product object, the value of the cell, and then any
            custom parameters defined in validation_rules array on the column
            definition. Note that they will be passed in as string values in
            the order they are specified. The function must return either a
            boolean (simplest), a Promise that resolves to a boolean (for async
            validation), or an object with a 'is_valid' property (boolean) and an
            optional 'msg_params' property (object whose keys/values are used
            to make custom replacements in the msg).

        An example to tie it all together:

        'do_stuff':
            msg: ":column :action :something!\"
            input_types: ['text', 'autcomplete']
            isValid: (value, column, product, action, something) =>
                if value? and value isnt ''
                    return true
                else
                    if column.input_type is 'autocomplete'
                        something = something.replace('peck', 'value')
                            .replace('of pickled peppers', 'from a dropdown')
                    return {
                        is_valid: false
                        msg_params:
                            'action': action
                            'something': () ->
                                return something
                    }

        A column using this validation rule could be defined as follows:

        display_name: 'Peter Piper'
        input_type: 'autocomplete'
        validation_rules:
            'do_stuff:picks,a peck of pickled peppers'

        If the cell is blank, this would result in the following validation
        message being displayed:
        "Peter Piper picks a value from a dropdown!"

        If the column definition was as follows:

        display_name: 'Mr. T'
        input_type: 'text'
        validation_rules:
            'do_stuff:pities,the foo'

        the resulting message would be : "Mr. T pities the foo!"
        ###
        @rules =

            'numeric':
                msg: ':column must be a number'
                isValid: (value, product, column) =>
                    if not value? or value is ''
                        return true
                    return $.isNumeric(value)

            'integer':
                msg: ':column must be a whole number'
                isValid: (value, product, column) =>
                    if not value? or value is ''
                        return true
                    return $.isNumeric(value) and (value % 1 is 0)

            'boolean':
                msg: ':column must be a true/false value'
                isValid: (value, product, column) =>
                    if not value? or value is ''
                        return true
                    return value in [true, false]

            'min':
                msg: ':column cannot be :comparison :min :units'
                input_types: ['numeric', 'text']
                isValid: (value, product, column, min) =>
                    if not value? or value is ''
                        return true
                    min = parseFloat(min)
                    switch column.input_type
                        when 'numeric'
                            if value >= min
                                return true
                            comparison = 'less than'
                            units = ''
                        when 'text'
                            if value.length >= min
                                return true
                            comparison = 'shorter than'
                            units = 'characters'
                    return {
                        is_valid: false
                        msg_params:
                            'min': min
                            'comparison': comparison
                            'units': units
                    }

            'max':
                msg: ':column cannot be :comparison :max :units'
                input_types: ['numeric', 'text']
                isValid: (value, product, column, max) =>
                    if not value? or value is ''
                        return true
                    max = parseFloat(max)
                    switch column.input_type
                        when 'numeric'
                            if value <= max
                                return true
                            comparison = 'greater than'
                            units = ''
                        when 'text'
                            if value.length <= max
                                return true
                            comparison = 'longer than'
                            units = 'characters'
                    return {
                        is_valid: false
                        msg_params:
                            'max': max
                            'comparison': comparison
                            'units': units
                    }

            'min_column':
                msg: ':column cannot be less than :min_column'
                input_types: ['numeric']
                isValid: (value, product, column, min_prop) =>
                    if not $.isNumeric(value)
                        return true

                    min = product[min_prop]
                    if not $.isNumeric(min)
                        return true

                    if value >= min
                        return true

                    return {
                        is_valid: false
                        msg_params:
                            'min_column': @all_columns[min_prop].display_name
                    }

            'max_column':
                msg: ':column cannot be greater than :max_column'
                input_types: ['numeric']
                isValid: (value, product, column, max_property) =>
                    if not $.isNumeric(value)
                        return true

                    max = product[max_property]
                    if not $.isNumeric(max)
                        return true

                    if value <= max
                        return true

                    return {
                        is_valid: false
                        msg_params:
                            'max_column': @all_columns[max_property].display_name
                    }

            'in_options':
                msg: '\":value\" is not a valid option for :column'
                input_types: ['autocomplete']
                isValid: (value, product, column) =>
                    if not value? or value is ''
                        return true

                    # get the options
                    options = _.map column.getOptions(product), (option) ->
                        return option.toString().trim()

                    return (value.toString().trim() in options)

            'is_product':
                msg: '\":value\" is not a Product that exists in your lineup. To add it, click the Add Product button in the sidebar.'
                input_types: ['autocomplete']
                isValid: (value, product, column) =>
                    if not value? or value is ''
                        return true

                    return _.find(@products, full_name: value)?

            'unique_refuge_option':
                msg: ':value cannot be selected for more than once for the same Product.'
                input_types: ['autocomplete']
                isValid: (value, product, column) =>
                    refuge = _.find(@products, full_name: value)
                    if not refuge?
                        # if there isn't a product selected, validation passes
                        return true

                    # get the selected options
                    for prop in @column_groups.refuge_option
                        if prop is column.column_alias
                            # exclude the current value
                            continue

                        other_refuge = _.find(@products, full_name: product[prop])
                        if not other_refuge?
                            # if it's not a valid refuge product, move on
                            continue

                        if other_refuge is refuge
                            # It's a duplicate refuge product! Oh no!
                            return false

                    return true

            'required':
                msg: ':column is required'
                isValid: (value, product, column) =>
                    if value? and value isnt ''
                        return true
                    return false

            'is_valid_refuge':
                msg: 'This product cannot be used as a refuge option because you have indicated that it requires a structured refuge.'
                input_types: ['checkbox']
                isValid: (value, product, column) =>
                    if not value
                        return true

                    return product.isAValidRefugeOption(@column_groups.refuge_percent)

            'has_refuge_available':
                msg: 'You have indicated that :product is tolerant to :chemistry_traits, but have not defined a compatible Refuge Product. You must either define a Refuge Product with :chemistry_options tolerance, or change :chemistry_traits to 0 to ensure that this product is not recommended for use with :chemistry_options.'
                input_types: ['autocomplete']
                isValid: (value, product, column) =>
                    # refuge products don't require refuge options
                    if product.isAValidRefugeOption(@column_groups.refuge_percent)
                        return true

                    # Only one refuge option column should be validated.
                    # If this isn't the right refuge column, validation passes.
                    if column.column_alias in @column_groups.refuge_option
                        for prop in @column_groups.refuge_option
                            column_to_validate = prop
                            if prop is column.column_alias
                                # use the new value, not the old one
                                if not value? or value is ''
                                    break
                            else
                                if not product[prop]? or product[prop] is ''
                                    break
                        if column.column_alias isnt column_to_validate
                            return true

                    # get the available options
                    available_options = column.getOptions(product)
                    refuge_products = available_options.map (product_name) =>
                        return _.find(@products, full_name: product_name)

                    # see which chemistry trait are missing
                    missing_traits = product.getMissingRefugeChemistries(refuge_products, @column_groups.chemistry)
                    if missing_traits.length is 0
                        return true

                    missing_traits = missing_traits.map (prop) =>
                        return @all_columns[prop].display_name

                    return {
                        is_valid: false
                        msg_params:
                            'chemistry_traits': array_utils.joinPretty(missing_traits)
                            'chemistry_options': array_utils.joinPretty(missing_traits, 'and/or')
                    }

            'has_refuge_selected':
                msg: 'You have indicated that :product is tolerant to :chemistry_traits, but have not selected a compatible Refuge Option. You must either select a Refuge Option with :chemistry_options tolerance, or change :chemistry_traits to 0 to ensure that this product is not recommended for use with :chemistry_options.'
                input_types: ['autocomplete']
                isValid: (value, product, column) =>
                    # refuge products don't require refuge options
                    if product.isAValidRefugeOption(@column_groups.refuge_percent)
                        return true

                    # Only one refuge option column should be validated.
                    # If this isn't the right refuge column, validation passes.
                    if column.column_alias in @column_groups.refuge_option
                        for prop in @column_groups.refuge_option
                            column_to_validate = prop
                            if prop is column.column_alias
                                # use the new value, not the old one
                                if not value? or value is ''
                                    break
                            else
                                if not product[prop]? or product[prop] is ''
                                    break
                        if column.column_alias isnt column_to_validate
                            return true

                    # get the available options
                    available_options = column.getOptions(product)
                    available_products = available_options.map (product_name) =>
                        return _.find(@products, full_name: product_name)

                    # We don't want to display both the refuge available and
                    # refuge selected validation messages. So if there aren't
                    # adequate options available, this validation passes.
                    missing_traits = product.getMissingRefugeChemistries(available_products, @column_groups.chemistry)
                    if missing_traits.length > 0
                        return true

                    # get the selected options
                    selected_products = @column_groups.refuge_option.map (prop) =>
                        if prop is column.column_alias
                            # use the new value, not the old one
                            return _.find(@products, full_name: value)
                        else
                            return _.find(@products, full_name: product[prop])
                    .filter (refuge_product) ->
                        return refuge_product?

                    # see which chemistry trait are missing
                    missing_traits = product.getMissingRefugeChemistries(selected_products, @column_groups.chemistry)
                    if missing_traits.length is 0
                        return true

                    missing_traits = missing_traits.map (prop) =>
                        return @all_columns[prop].display_name

                    return {
                        is_valid: false
                        msg_params:
                            'chemistry_traits': array_utils.joinPretty(missing_traits)
                            'chemistry_options': array_utils.joinPretty(missing_traits, 'and/or')
                    }

    validate: (rule_str, cell, product, column, get_messages, callback) =>
        # Parse the rule string
        [rule, rule_obj, rule_params] = @parseRuleString(rule_str, column)

        # Run the validation rule
        # NOTE: ALWAYS use `cell.value` to get the value. Doing
        # `product[column.column_alias]` returns the old value, not the new one.
        result = rule_obj.isValid(cell.value, product, column, rule_params...)

        # Interpret the result
        # this is a function just so we don't have to repeat this code
        doCallback = (is_valid, msg_params = {}) =>
            if is_valid
                callback(true)
            else if not get_messages
                callback(false)
            else
                replacements = @evaluateReplacements(cell, product, column, msg_params)

                # do any replacements
                msg = rule_obj.msg
                for key, val of replacements
                    msg = msg.replace(":#{key}", val)

                callback(false, msg)

        switch typeof result
            when 'boolean'
                doCallback(result)
            when 'object'
                if result.is_valid?
                    doCallback(result.is_valid, result.msg_params)
                else if result.then?
                    result.then (is_valid) =>
                        doCallback(is_valid)
                else
                    # who knows what we got back??
                    throw "Error: the '#{rule}' validation rule returned an invalid result for the '#{column.display_name}' column (value was '#{cell.value}'). The result must be a boolean, a Promise object, or an object with an 'is_valid' property."

    parseRuleString: (rule_str, column) =>
        # Validation rules are defined in the following format:
        # "rule_name[:param1[,param2...]]"
        [rule, param_str] = rule_str.split(':', 2)

        # Make sure the rule exists
        if not @rules[rule]?
            throw "Error: '#{rule}' is not defined as a validation rule. In order to use it, you must add it to the RatingsValidator."

        rule_obj = @rules[rule]

        # Make sure the rule object is valid
        if typeof rule_obj.isValid isnt 'function' or typeof rule_obj.msg isnt 'string'
            throw "Error: '#{rule}' is not a valid validation rule. The definition must include an 'isValid' method and a 'msg' property."

        # make sure the rule can be applied to the input
        if rule_obj.input_types?.length > 0
            if column.input_type not in rule_obj.input_types
                input_types_str = array_utils.joinPretty rule_obj.input_types.map (input_type) ->
                    return "'#{input_type}'"
                throw "Error: the '#{rule}' validation rule cannot be applied to the '#{column.display_name}' column because the input_type is '#{column.input_type}' input type. Only #{input_types_str} columns can be used with the '#{rule}' rule."

        # Parse out any rule-specific parameters from the string
        if param_str?.length
            rule_params = param_str.split(',').map (val) ->
                return val.trim() or null

        return [rule, rule_obj, rule_params ? []]


    evaluateReplacements: (cell, product, column, msg_params) =>
        # predefined replacements
        replacements =
            'value': cell.value
            'product': product.full_name
            'column': column.display_name

        for key, val of msg_params
            if typeof val is 'function'
                replacements[key] = val()
            else
                replacements[key] = val

        return replacements
